import React, { useState } from 'react';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { Button, Spinner, Form } from '@/components/ui';

interface ExportOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPreliminaryExport: (customFilename?: string, reportType?: string) => void;
  onFinalExport?: (postedDate: string, customFilename?: string, reportType?: string) => void;
  loadingPreliminary: boolean;
  loadingFinal?: boolean;
  isWildlife?: boolean;
  isSigns?: boolean;
  isCulverts?: boolean;
  isCattleguard?: boolean;
  isEquipmentUsage?: boolean;
  isMaterialUsage?: boolean;
  isRockfall?: boolean;
  isPayroll?: boolean;
  defaultFilename?: string;
  generateFilename?: (isPreliminary: boolean) => string;
  generateEquipmentUsageFilename?: (reportType: 'simple' | 'sage-ready') => string;
  generateMaterialUsageFilename?: (reportType: 'simple' | 'sage-ready') => string;
  showFilenameInput?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const ExportOptionsModal: React.FC<ExportOptionsModalProps> = ({
  isOpen,
  onClose,
  onPreliminaryExport,
  onFinalExport,
  loadingPreliminary,
  loadingFinal = false,
  isWildlife = false,
  isSigns = false,
  isCulverts = false,
  isCattleguard = false,
  isEquipmentUsage = false,
  isMaterialUsage = false,
  isRockfall = false,
  isPayroll = false,
  defaultFilename = '',
  generateFilename,
  generateEquipmentUsageFilename,
  generateMaterialUsageFilename,
  showFilenameInput = false,
  size = 'small'
}) => {
  const needsCustomFilename = isWildlife || isSigns || isCulverts || isCattleguard || isEquipmentUsage || isMaterialUsage || isRockfall;
  const hasMultipleOptions = isPayroll || !needsCustomFilename;
  
  const [exportType, setExportType] = useState<'preliminary' | 'final'>(
    needsCustomFilename ? 'final' : 'preliminary'
  );
  const [postedDate, setPostedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  });

  const [customFilename, setCustomFilename] = useState(() => {
    if (isEquipmentUsage && generateEquipmentUsageFilename) {
      return generateEquipmentUsageFilename('simple');
    }
    if (isMaterialUsage && generateMaterialUsageFilename) {
      return generateMaterialUsageFilename('simple');
    }
    return generateFilename ? generateFilename(exportType === 'preliminary') : defaultFilename;
  });

  // New state for equipment/material usage report type selection
  const [reportType, setReportType] = useState<'simple' | 'sage-ready'>('simple');

  // Update custom filename when default changes or export type changes
  React.useEffect(() => {
    if (isEquipmentUsage && generateEquipmentUsageFilename) {
      setCustomFilename(generateEquipmentUsageFilename(reportType));
    } else if (isMaterialUsage && generateMaterialUsageFilename) {
      setCustomFilename(generateMaterialUsageFilename(reportType));
    } else if (generateFilename) {
      setCustomFilename(generateFilename(exportType === 'preliminary'));
    } else {
      setCustomFilename(defaultFilename);
    }
  }, [defaultFilename, generateFilename, generateEquipmentUsageFilename, generateMaterialUsageFilename, exportType, reportType, isEquipmentUsage, isMaterialUsage]);

  const handleExport = () => {
    if (exportType === 'preliminary') {
      onPreliminaryExport(
        showFilenameInput ? customFilename : undefined,
        (isEquipmentUsage || isMaterialUsage) ? reportType : undefined
      );
    } else if (onFinalExport) {
      onFinalExport(
        postedDate, 
        showFilenameInput ? customFilename : undefined,
        (isEquipmentUsage || isMaterialUsage) ? reportType : undefined
      );
    }
  };

  const isDuringExport = loadingPreliminary || loadingFinal;
  const isExportDisabled =
    isDuringExport || (exportType === 'final' && !needsCustomFilename && !isPayroll && !postedDate);

  const finalExportLabel = isPayroll ? 'Finalized' : 'Final';
  const getExportButtonLabel = () => {
    if (needsCustomFilename) return 'Export Report';
    if (exportType === 'final') {
      return isPayroll ? 'Export Finalized Report' : 'Export Final Report';
    }
    return 'Export Preliminary Report';
  };

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} title="Export Options" size={size}>
      <div className="tw-mb-4">
        {/* Export Type Selection - Only show when multiple options are available */}
        {hasMultipleOptions && (
          <div className="tw-mb-3">
            <Form.Label className="tw-font-bold tw-mb-2">Export Type</Form.Label>

            <Form.Check
              type="radio"
              id="export-preliminary"
              name="exportType"
              label="Preliminary"
              checked={exportType === 'preliminary'}
              onChange={() => setExportType('preliminary')}
              className="tw-mb-2"
            />

            <Form.Check
              type="radio"
              id="export-final"
              name="exportType"
              label={finalExportLabel}
              checked={exportType === 'final'}
              onChange={() => setExportType('final')}
              className="tw-mb-3"
            />

            {/* Description based on selected type */}
            <div className="tw-text-content-muted tw-text-sm tw-mb-3">
              {exportType === 'preliminary' &&
                (isPayroll
                  ? 'Download CSV without marking forms as exported'
                  : 'Keep Task Statuses as they are')}
              {exportType === 'final' &&
                (isPayroll
                  ? 'Download CSV, mark forms as exported, and save to export history'
                  : 'Move all in-scope Task Statuses to Finalized')}
            </div>
          </div>
        )}

        {/* Report Type Selection - Show for equipment usage and material usage reports */}
        {(isEquipmentUsage || isMaterialUsage) && (
          <div className="tw-mb-3">
            <Form.Label className="tw-font-bold tw-mb-2">Report Format</Form.Label>

            <Form.Check
              type="radio"
              id="report-simple"
              name="reportType"
              label="Simple"
              checked={reportType === 'simple'}
              onChange={() => setReportType('simple')}
              className="tw-mb-2"
            />

            <Form.Check
              type="radio"
              id="report-sage-ready"
              name="reportType"
              label="Sage-ready"
              checked={reportType === 'sage-ready'}
              onChange={() => setReportType('sage-ready')}
              className="tw-mb-3"
            />

            {/* Description based on selected report type */}
            <div className="tw-text-content-muted tw-text-sm tw-mb-3">
              {reportType === 'simple' && `Standard ${isMaterialUsage ? 'material' : 'equipment'} usage report format with regular column headers`}
              {reportType === 'sage-ready' && 'Special format with numbered columns (Column 1, Column 2, etc.) for Sage import'}
            </div>
          </div>
        )}

        {/* Conditional Fields */}
        <div className="tw-mb-3">
          {/* Custom Filename Input - Show for both preliminary and final when showFilenameInput is true */}
          {showFilenameInput && (
            <div className="tw-mb-3">
              <Form.Label className="tw-text-sm tw-mb-1">Filename</Form.Label>
              <Form.Control
                type="text"
                value={customFilename}
                onChange={(e) => setCustomFilename(e.target.value)}
                size="sm"
                placeholder="Enter custom filename"
              />
            </div>
          )}

          {/* Posted Date Input - Only show for final exports of D2/D3 (not payroll) */}
          {exportType === 'final' && !needsCustomFilename && !isPayroll && (
            <div className="tw-mb-3">
              <Form.Label className="tw-text-sm tw-mb-1">
                Posted Date <span className="tw-text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={postedDate}
                onChange={(e) => setPostedDate(e.target.value)}
                size="sm"
                required
                isInvalid={!postedDate}
              />
              <Form.Control.Feedback type="invalid">
                Posted Date is required for Final exports
              </Form.Control.Feedback>
            </div>
          )}
        </div>
      </div>

      {/* Single Export Button */}
      <div className="tw-grid">
        <Button
          variant={exportType === 'preliminary' ? 'primary' : 'success'}
          onClick={handleExport}
          disabled={isExportDisabled}
          size="lg"
        >
          {isDuringExport && <Spinner animation="border" size="sm" className="tw-mr-2" />}
          {getExportButtonLabel()}
        </Button>
      </div>
    </ResponsiveModal>
  );
};

export default ExportOptionsModal; 